import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import {
  HiOutlineArrowDownLeft,
  HiOutlineArrowUpRight,
  HiOutlineChatBubbleLeftRight,
  HiOutlineCheckCircle,
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlinePlusCircle,
  HiOutlineXMark,
} from 'react-icons/hi2';

interface Message {
  id: number;
  appointmentId?: number | null;
  leadName: string;
  leadEmail: string;
  leadPhone?: string | null;
  contactMethod: string;
  direction: string;
  subject?: string | null;
  body: string;
  status: string;
  adminNote?: string | null;
  createdAt: string;
}

const getApiBase = () => {
  const base = import.meta.env.BASE_URL?.replace(/\/$/, '') || '';
  return `${base}/api`;
};

function statusColor(s: string) {
  switch (s) {
    case 'replied':
      return 'bg-green-100 text-green-700';
    case 'delivered':
      return 'bg-blue-100 text-blue-700';
    case 'read':
      return 'bg-purple-100 text-purple-700';
    case 'no-response':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

function MethodIcon({ method }: { method: string }) {
  switch (method) {
    case 'phone':
      return <HiOutlinePhone className="w-4 h-4" />;
    case 'sms':
      return <HiOutlineChatBubbleLeftRight className="w-4 h-4" />;
    default:
      return <HiOutlineEnvelope className="w-4 h-4" />;
  }
}

export default function AdminMessages() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showCompose, setShowCompose] = useState(false);
  const [compose, setCompose] = useState({
    leadName: '',
    leadEmail: '',
    leadPhone: '',
    contactMethod: 'email',
    direction: 'outbound',
    subject: '',
    body: '',
    adminNote: '',
  });

  const { data: messages, isLoading } = useQuery<Message[]>({
    queryKey: ['messages'],
    queryFn: async () => {
      const res = await fetch(`${getApiBase()}/messages`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load messages');
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof compose) => {
      const res = await fetch(`${getApiBase()}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to log contact');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      setShowCompose(false);
      setCompose({
        leadName: '',
        leadEmail: '',
        leadPhone: '',
        contactMethod: 'email',
        direction: 'outbound',
        subject: '',
        body: '',
        adminNote: '',
      });
      toast({ title: 'Contact logged successfully' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      status,
      adminNote,
    }: {
      id: number;
      status?: string;
      adminNote?: string;
    }) => {
      const res = await fetch(`${getApiBase()}/messages/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status, adminNote }),
      });
      if (!res.ok) throw new Error('Failed to update');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  const set =
    (k: string) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) =>
      setCompose(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="w-5 h-px bg-[#0ea5e9]" />
            <span className="font-rajdhani font-bold text-[0.64rem] tracking-[0.3em] uppercase text-[#0ea5e9]">
              Lead Management
            </span>
          </div>
          <h1 className="font-bebas text-[2.6rem] leading-none tracking-[0.03em] text-[#0a2540]">
            Contacts & Outreach
          </h1>
          <p className="font-sans font-light text-sm text-[#94a3b8] mt-1">
            Log calls, emails, and follow-ups with leads and enrolled students.
          </p>
        </div>
        <button
          onClick={() => setShowCompose(v => !v)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full font-rajdhani font-bold text-[0.74rem] tracking-[0.18em] uppercase text-white cursor-pointer transition-all hover:-translate-y-0.5"
          style={{
            background: 'linear-gradient(135deg, #0ea5e9, #0369a1)',
            boxShadow: '0 4px 16px rgba(14,165,233,0.35)',
          }}
        >
          <HiOutlinePlusCircle className="w-4 h-4" />
          Log Contact
        </button>
      </div>

      {/* Compose form */}
      {showCompose && (
        <div
          className="bg-white rounded-2xl p-7 mb-6"
          style={{ boxShadow: '0 8px 32px rgba(10,37,64,0.1)' }}
        >
          <div className="flex items-center justify-between mb-5">
            <div className="font-rajdhani font-bold text-[0.88rem] tracking-[0.08em] uppercase text-[#0a2540]">
              New Contact Log
            </div>
            <button
              onClick={() => setShowCompose(false)}
              className="text-[#94a3b8] hover:text-[#0a2540] cursor-pointer"
            >
              <HiOutlineXMark className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                label: 'Lead Name',
                k: 'leadName',
                placeholder: 'Full Name',
                type: 'text',
              },
              {
                label: 'Email',
                k: 'leadEmail',
                placeholder: 'email@example.com',
                type: 'email',
              },
              {
                label: 'Phone',
                k: 'leadPhone',
                placeholder: '+1 (555) 000-0000',
                type: 'tel',
              },
              {
                label: 'Subject',
                k: 'subject',
                placeholder: 'Contact subject',
                type: 'text',
              },
            ].map(f => (
              <div key={f.k}>
                <label className="font-rajdhani font-bold text-[0.62rem] tracking-[0.22em] uppercase text-[#94a3b8] block mb-1.5">
                  {f.label}
                </label>
                <input
                  type={f.type}
                  value={(compose as Record<string, string>)[f.k]}
                  onChange={set(f.k)}
                  placeholder={f.placeholder}
                  className="w-full h-11 px-4 rounded-xl bg-[#f7fafd] font-sans text-sm text-[#0d1e30] outline-none focus:ring-2 focus:ring-[#0ea5e9]/25"
                />
              </div>
            ))}
            <div>
              <label className="font-rajdhani font-bold text-[0.62rem] tracking-[0.22em] uppercase text-[#94a3b8] block mb-1.5">
                Method
              </label>
              <select
                value={compose.contactMethod}
                onChange={set('contactMethod')}
                className="w-full h-11 px-4 rounded-xl bg-[#f7fafd] font-sans text-sm text-[#0d1e30] outline-none"
              >
                <option value="email">Email</option>
                <option value="phone">Phone Call</option>
                <option value="sms">SMS</option>
                <option value="in-person">In Person</option>
              </select>
            </div>
            <div>
              <label className="font-rajdhani font-bold text-[0.62rem] tracking-[0.22em] uppercase text-[#94a3b8] block mb-1.5">
                Direction
              </label>
              <select
                value={compose.direction}
                onChange={set('direction')}
                className="w-full h-11 px-4 rounded-xl bg-[#f7fafd] font-sans text-sm text-[#0d1e30] outline-none"
              >
                <option value="outbound">Outbound (we contacted them)</option>
                <option value="inbound">Inbound (they contacted us)</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="font-rajdhani font-bold text-[0.62rem] tracking-[0.22em] uppercase text-[#94a3b8] block mb-1.5">
              Message / Notes
            </label>
            <textarea
              value={compose.body}
              onChange={set('body')}
              placeholder="What was discussed, outcome, next steps..."
              rows={3}
              className="w-full px-4 py-3 rounded-xl bg-[#f7fafd] font-sans text-sm text-[#0d1e30] outline-none focus:ring-2 focus:ring-[#0ea5e9]/25 resize-none"
            />
          </div>
          <div className="mt-4">
            <label className="font-rajdhani font-bold text-[0.62rem] tracking-[0.22em] uppercase text-[#94a3b8] block mb-1.5">
              Internal Admin Note
            </label>
            <textarea
              value={compose.adminNote}
              onChange={set('adminNote')}
              placeholder="Private note for your team..."
              rows={2}
              className="w-full px-4 py-3 rounded-xl bg-[#f7fafd] font-sans text-sm text-[#0d1e30] outline-none focus:ring-2 focus:ring-[#0ea5e9]/25 resize-none"
            />
          </div>
          <div className="flex gap-3 mt-5">
            <button
              onClick={() => createMutation.mutate(compose)}
              disabled={
                createMutation.isPending || !compose.leadEmail || !compose.body
              }
              className="px-6 py-2.5 rounded-full font-rajdhani font-bold text-[0.74rem] tracking-[0.18em] uppercase text-white flex items-center gap-2 cursor-pointer disabled:opacity-50"
              style={{ background: '#0ea5e9' }}
            >
              {createMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <HiOutlineCheckCircle className="w-4 h-4" />
              )}
              Save Contact Log
            </button>
            <button
              onClick={() => setShowCompose(false)}
              className="px-6 py-2.5 rounded-full font-rajdhani font-bold text-[0.74rem] tracking-[0.18em] uppercase text-[#94a3b8] cursor-pointer hover:text-[#0a2540]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Messages list */}
      <div className="space-y-4">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))
        ) : Array.isArray(messages) && messages.length > 0 ? (
          messages.map(msg => (
            <div
              key={msg.id}
              className="bg-white rounded-2xl p-6 transition-all"
              style={{ boxShadow: '0 4px 20px rgba(10,37,64,0.06)' }}
            >
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#e0f4fd] flex items-center justify-center shrink-0">
                    <MethodIcon method={msg.contactMethod} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-rajdhani font-bold text-[0.9rem] text-[#0a2540]">
                        {msg.leadName}
                      </span>
                      <span className="font-sans text-[0.78rem] text-[#94a3b8]">
                        {msg.leadEmail}
                      </span>
                      {msg.leadPhone && (
                        <span className="font-sans text-[0.78rem] text-[#94a3b8]">
                          {msg.leadPhone}
                        </span>
                      )}
                    </div>
                    {msg.subject && (
                      <div className="font-rajdhani font-bold text-[0.8rem] tracking-[0.06em] text-[#0a2540] mb-1">
                        {msg.subject}
                      </div>
                    )}
                    <p className="font-sans text-[0.82rem] text-[#94a3b8] leading-[1.6] mb-2">
                      {msg.body}
                    </p>
                    {msg.adminNote && (
                      <div className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 px-3 py-1 rounded-lg text-[0.74rem] font-sans">
                        <span className="font-bold">Note:</span> {msg.adminNote}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <div className="flex items-center gap-2">
                    {msg.direction === 'inbound' ? (
                      <span className="flex items-center gap-1 text-[0.64rem] font-rajdhani font-bold uppercase tracking-[0.14em] text-green-600">
                        <HiOutlineArrowDownLeft className="w-3 h-3" />
                        Inbound
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-[0.64rem] font-rajdhani font-bold uppercase tracking-[0.14em] text-blue-600">
                        <HiOutlineArrowUpRight className="w-3 h-3" />
                        Outbound
                      </span>
                    )}
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[0.62rem] font-rajdhani font-bold uppercase tracking-[0.14em] ${statusColor(msg.status)}`}
                    >
                      {msg.status}
                    </span>
                  </div>
                  <span className="font-sans text-[0.72rem] text-[#94a3b8]">
                    {format(new Date(msg.createdAt), 'MMM d, yyyy')}
                  </span>
                  <select
                    value={msg.status}
                    onChange={e =>
                      updateMutation.mutate({
                        id: msg.id,
                        status: e.target.value,
                      })
                    }
                    className="text-[0.72rem] font-rajdhani font-bold uppercase tracking-[0.08em] text-[#0369a1] bg-transparent outline-none cursor-pointer"
                  >
                    <option value="sent">Sent</option>
                    <option value="delivered">Delivered</option>
                    <option value="read">Read</option>
                    <option value="replied">Replied</option>
                    <option value="no-response">No Response</option>
                  </select>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div
            className="text-center py-20 bg-white rounded-2xl"
            style={{ boxShadow: '0 4px 20px rgba(10,37,64,0.06)' }}
          >
            <HiOutlineEnvelope className="w-10 h-10 text-[#0ea5e9] mx-auto mb-3" />
            <div className="font-bebas text-2xl text-[#0a2540] mb-2">
              No Contacts Yet
            </div>
            <p className="font-sans text-[#94a3b8] text-sm">
              Use the "Log Contact" button to record outreach to leads or
              enrolled students.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
